import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Phone, Leaf, ArrowRight, MapPin, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const prefill = location.state?.prefill || {};

  const [phone, setPhone] = useState(prefill.phone || '');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const { sendOtp, phoneLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhoneForDisplay = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.slice(0, 10);
  };

  const getE164Phone = () => `+91${phone.replace(/\D/g, '')}`;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');

    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10 || !/^[6-9]/.test(digits)) {
      setError('Please enter a valid Indian mobile number (must start with 6, 7, 8, or 9)');
      return;
    }

    setIsLoading(true);
    try {
      const result = await sendOtp(getE164Phone());
      if (result.success) {
        setStep('otp');
        setCountdown(result.cooldownSeconds || 60);
      } else {
        setError(result.error || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);
    try {
      const result = await phoneLogin(getE164Phone(), otp);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError('OTP verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setError('');
    setIsLoading(true);
    try {
      const result = await sendOtp(getE164Phone());
      if (result.success) {
        setCountdown(result.cooldownSeconds || 60);
        setOtp('');
      } else {
        setError(result.error || 'Failed to resend OTP.');
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#1a2f0f] via-[#2e4e18] to-[#3f6b20] overflow-hidden">
        {/* Decorative background circles */}
        <div className="absolute -top-28 -right-28 w-[440px] h-[440px] rounded-full bg-white/5" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-16 w-52 h-52 rounded-full bg-[#FF9800]/10" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
          {/* Logo */}
          <Link to="/">
            <img src="/logo.svg" alt="BookMyFresh" className="h-14 w-auto" />
          </Link>

          {/* Main content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-6">
              <Leaf className="w-4 h-4 text-[#8BC34A]" />
              <span className="text-sm font-medium text-white/90">Jaipur's Fresh Produce App</span>
            </div>
            <h1 className="text-4xl font-bold mb-4 leading-snug">
              Welcome back to<br />BookMyFresh
            </h1>
            <p className="text-white/70 text-lg leading-relaxed max-w-sm">
              Fresh fruits and vegetables delivered from farms and trusted suppliers — directly to your doorstep.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 text-center">
            {[
              { value: 'Free', label: 'Instant Orders' },
              { value: '2', label: 'Delivery Types' },
              { value: 'Daily', label: 'Fresh Stock' },
            ].map(({ value, label }) => (
              <div key={label} className="bg-white/10 rounded-2xl py-4 px-2">
                <div className="text-2xl font-bold text-[#8BC34A]">{value}</div>
                <div className="text-xs text-white/60 mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/shop" className="inline-flex items-center gap-2">
              <img src="/logo-symbol.png" alt="Book my fresh" className="h-10 w-auto" />
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Sign In</h2>
            <p className="text-gray-600 mb-4">
              {step === 'phone' ? 'Enter your mobile number to get started.' : 'Enter the OTP sent to your phone.'}
            </p>

            {/* Jaipur Delivery Note */}
            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-700 text-sm">
                <span className="font-medium">Note:</span> Currently, we deliver only to Jaipur locations.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {step === 'phone' ? (
              <form onSubmit={handleSendOtp} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <div className="relative flex">
                    <span className="inline-flex items-center px-4 py-3 border border-r-0 border-gray-300 rounded-l-xl bg-gray-50 text-gray-600 text-sm font-medium">
                      +91
                    </span>
                    <div className="relative flex-1">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(formatPhoneForDisplay(e.target.value))}
                        placeholder="Enter 10-digit mobile number"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-r-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all"
                        maxLength={10}
                        required
                      />
                    </div>
                  </div>
                </div>

                <button
                  id="send-otp-button"
                  type="submit"
                  disabled={isLoading || phone.replace(/\D/g, '').length !== 10}
                  className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Send OTP
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Enter OTP
                    </label>
                    <span className="text-sm text-gray-500">
                      Sent to +91 {phone}
                    </span>
                  </div>
                  <div className="relative">
                    <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all text-center text-lg tracking-widest"
                      maxLength={6}
                      autoFocus
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Verify & Sign In
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    ← Change number
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={countdown > 0 || isLoading}
                    className="text-[#548B2E] hover:text-[#3f6b20] font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#548B2E] hover:text-[#3f6b20] font-semibold">
                  Sign up for free
                </Link>
              </p>
            </div>
          
            {/* <div className="mt-4 pt-4 border-t border-gray-100 text-center">
              <Link
                to="/admin/login"
                className="text-xs text-gray-400 hover:text-[#548B2E] transition-colors"
              >
                Login as Admin
              </Link>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;